import { Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, FileText, Zap, Download, Mail, Clock, Shield } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Is Invilic really free to use?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic is free for up to 5 invoices per month with no credit card required. You can create and download professional PDF invoices without signing up.',
      },
    },
    {
      '@type': 'Question',
      name: 'What types of invoices can I create for free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'You can create GST invoices (India), VAT invoices (UK/EU/Australia), standard freelancer invoices, coach and consultant invoices, and international invoices, all free.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to create an account?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No account is required for basic use. You can create and download up to 5 invoices per month without signing up. An account unlocks invoice history, email delivery, and shareable links.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I download the invoice as a PDF?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Every invoice you create on Invilic can be downloaded as a professionally formatted PDF file instantly, for free.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is the Pro plan and how much does it cost?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'The Pro plan costs $6/month and includes unlimited invoices, email delivery to clients, invoice history, CSV export, and shareable invoice links.',
      },
    },
  ],
};

const features = [
  { icon: FileText, text: 'GST, VAT, freelancer & international formats' },
  { icon: Download, text: 'Instant PDF download, no watermarks' },
  { icon: Zap, text: 'Create a complete invoice in under 2 minutes' },
  { icon: Shield, text: 'No sign-up required for first 5 invoices' },
  { icon: Mail, text: 'Email invoices to clients (Pro)' },
  { icon: Clock, text: 'Invoice history & CSV export (Pro)' },
];

const steps = [
  { n: '1', title: 'Choose invoice type', desc: 'Pick from GST, VAT, freelancer, or international.' },
  { n: '2', title: 'Fill in your details', desc: 'Add your business info, client details, and line items.' },
  { n: '3', title: 'Download PDF', desc: 'Get a professional PDF invoice instantly, completely free.' },
];

export default function FreeInvoiceGeneratorPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="Free Invoice Generator — No Account Required | Create Invoices Online | Invilic"
        description="Create professional invoices free online — no account, no login, no signup. GST, VAT, freelancer & international formats. Download PDF instantly. 5 invoices free/month."
        canonical="https://www.invilic.com/free-invoice-generator"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            Free · No account · No login required
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            Free Invoice Generator — No Account Required
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create professional invoices online in minutes with no signup and no login. Download as PDF instantly — GST, VAT, freelancer, and international formats all included. Free for up to 5 invoices per month, no credit card required.
          </p>
          <Link
            to="/create"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Free Invoice <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">No credit card · No account required</p>
        </div>

        {/* Invoice types quick nav */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-16">
          {[
            { label: 'GST Invoice', to: '/gst-invoice-generator', color: 'emerald' },
            { label: 'VAT Invoice', to: '/vat-invoice-generator', color: 'orange' },
            { label: 'Freelancer', to: '/freelancer-invoice-generator', color: 'blue' },
            { label: 'International', to: '/international-invoice-generator', color: 'indigo' },
            { label: 'Coach & Consultant', to: '/coach-invoice-generator', color: 'violet' },
          ].map(({ label, to, color }) => (
            <Link
              key={label}
              to={to}
              className={`text-center text-sm font-medium px-4 py-3 rounded-xl border border-${color}-100 dark:border-${color}-800/40 text-${color}-700 dark:text-${color}-300 bg-${color}-50 dark:bg-${color}-900/20 hover:bg-${color}-100 dark:hover:bg-${color}-900/40 transition-colors`}
            >
              {label}
            </Link>
          ))}
        </div>

        {/* How it works */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How to Create a Free Invoice in 3 Steps
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {steps.map(({ n, title, desc }) => (
              <div key={n} className="text-center p-6 rounded-2xl border border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50">
                <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold text-lg flex items-center justify-center mx-auto mb-4">
                  {n}
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Everything You Need in a Free Invoice Maker
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {features.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm text-gray-700 dark:text-gray-300">{text}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing callout */}
        <section className="mb-16 rounded-2xl border border-blue-100 dark:border-blue-800/40 bg-blue-50 dark:bg-blue-900/20 p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Simple, Transparent Pricing</h2>
          <div className="grid sm:grid-cols-2 gap-6 max-w-lg mx-auto">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-gray-100 dark:border-gray-800">
              <div className="text-2xl font-extrabold text-gray-900 dark:text-white mb-1">Free</div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">No credit card needed</div>
              {['5 invoices/month', 'All invoice types', 'PDF download'].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" /> {f}
                </div>
              ))}
            </div>
            <div className="bg-blue-600 rounded-xl p-5 text-white">
              <div className="text-2xl font-extrabold mb-1">$6<span className="text-base font-normal">/month</span></div>
              <div className="text-sm text-blue-200 mb-4">Pro plan</div>
              {['Unlimited invoices', 'Email to clients', 'Invoice history', 'CSV export', 'Shareable links'].map(f => (
                <div key={f} className="flex items-center gap-2 text-sm text-blue-100 mb-2">
                  <CheckCircle className="w-4 h-4 text-white flex-shrink-0" /> {f}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details
                key={name}
                className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Start Creating Your Free Invoice Now
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">No account required. Takes less than 2 minutes.</p>
          <Link
            to="/create"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/20 transition-all duration-200 text-lg"
          >
            Create Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

